import React from 'react';
import type {
  WeeklyLearningGuideExportPayload,
  WeeklyLearningGuideExportPeriod,
  WeeklyLearningGuideExportWeekBlock,
} from '../../utils/weeklyLearningGuideExcelExport';
import {
  chunkWeeklyGuideTermWeekBlocks,
  computeWeeklyGuideA4LayoutStyle,
  NEIS_GRID_WEEKS_PER_PAGE,
} from '../../utils/weeklyLearningGuideA4Layout';
import { NEIS_SHORT_EDGE_HEX } from '../../utils/neisShortEdgeColor';
import {
  buildNeisShortIncreaseEdgeStyle,
  neisVsBaseBackgroundClass,
} from '../../utils/weeklyLearningGuideNeisCellStyle';
import { weeklyGuideSurfaceClassNames } from '../../utils/weeklyLearningGuideSurface';
import { subjectTrackTintRgba } from '../../utils/subjectTrackTint';
import WeeklyLearningGuideNeisCellContent from './WeeklyLearningGuideNeisCellContent';
import WeeklyLearningGuideNeisLegend from './WeeklyLearningGuideNeisLegend';

export type WeeklyLearningGuideA4DocumentProps = {
  payload: WeeklyLearningGuideExportPayload;
};

function subjectTintStyle(hex: string | undefined): React.CSSProperties | undefined {
  if (!hex?.trim()) return undefined;
  return { backgroundColor: subjectTrackTintRgba(hex) };
}

function neisCellEdgeStyle(
  periods: WeeklyLearningGuideExportPeriod[],
  periodIdx: number,
  colIdx: number
): React.CSSProperties | undefined {
  const cell = periods[periodIdx]!.cells[colIdx]!;
  if (!cell.neisShortIncreaseHighlight) return undefined;
  const topNeighbor =
    periodIdx > 0 && !!periods[periodIdx - 1]!.cells[colIdx]?.neisShortIncreaseHighlight;
  const bottomNeighbor =
    periodIdx < periods.length - 1 &&
    !!periods[periodIdx + 1]!.cells[colIdx]?.neisShortIncreaseHighlight;
  const rowCells = periods[periodIdx]!.cells;
  const leftNeighbor = colIdx > 0 && !!rowCells[colIdx - 1]?.neisShortIncreaseHighlight;
  const rightNeighbor = colIdx < rowCells.length - 1 && !!rowCells[colIdx + 1]?.neisShortIncreaseHighlight;
  return buildNeisShortIncreaseEdgeStyle(
    {
      self: true,
      topNeighbor,
      bottomNeighbor,
      leftNeighbor,
      rightNeighbor,
    },
    NEIS_SHORT_EDGE_HEX.light,
    'border'
  );
}

type WeeklyLearningGuideA4WeekTableProps = {
  block: WeeklyLearningGuideExportWeekBlock;
  neisMode: boolean;
  includeNotice: boolean;
};

function WeeklyLearningGuideA4WeekTable({ block, neisMode, includeNotice }: WeeklyLearningGuideA4WeekTableProps) {
  const { columns, periods, noticeText } = block;

  return (
    <table className="cp-weekly-learning-guide-a4-table">
      <thead>
        <tr>
          <th rowSpan={2} className="cp-weekly-learning-guide-a4-title-col">
            구분
          </th>
          {columns.map((column) => (
            <th key={`head-${column.weekdayLabel}-${column.dateHeader}`} className="cp-weekly-learning-guide-a4-day-head">
              {column.weekdayLabel}
            </th>
          ))}
        </tr>
        <tr>
          {columns.map((column) => (
            <th key={`date-${column.weekdayLabel}-${column.dateHeader}`} className="cp-weekly-learning-guide-a4-date-head">
              {column.dateHeader}
            </th>
          ))}
        </tr>
      </thead>

      {periods.map((period, periodIdx) => (
        <tbody
          key={period.label}
          className={
            neisMode
              ? 'cp-weekly-learning-guide-a4-period-tbody cp-weekly-learning-guide-a4-period-tbody--neis'
              : 'cp-weekly-learning-guide-a4-period-tbody'
          }
        >
          {neisMode ? (
            <tr className="cp-weekly-learning-guide-a4-tr cp-weekly-learning-guide-a4-tr--neis">
              <th className="cp-weekly-learning-guide-a4-period-title">{period.label}</th>
              {period.cells.map((cell, colIndex) => {
                const surfaceCls = weeklyGuideSurfaceClassNames(cell.slotSurface);
                const neisBgClass = neisVsBaseBackgroundClass(cell.neisVsBase, cell.slotSurface);
                return (
                  <td
                    key={`${period.label}-neis-${colIndex}`}
                    className={[
                      'cp-weekly-learning-guide-a4-cell',
                      'cp-weekly-learning-guide-a4-cell--center',
                      'cp-weekly-learning-guide-cell--neis-track',
                      surfaceCls,
                      neisBgClass,
                      cell.neisShortIncreaseHighlight ? 'cp-weekly-learning-guide-cell--neis-short-increase' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={neisCellEdgeStyle(periods, periodIdx, colIndex)}
                  >
                    <WeeklyLearningGuideNeisCellContent
                      slotSurface={cell.slotSurface}
                      subject={cell.subject}
                      neisTrackName={cell.neisTrackName}
                      neisUnregisteredTimetableText={cell.neisUnregisteredTimetableText}
                      neisDedicated={cell.neisDedicated}
                    />
                  </td>
                );
              })}
            </tr>
          ) : (
            <>
              <tr className="cp-weekly-learning-guide-a4-tr cp-weekly-learning-guide-a4-tr--1line">
                <th rowSpan={5} className="cp-weekly-learning-guide-a4-period-title">
                  {period.label}
                </th>
                {period.cells.map((cell, colIndex) => {
                  const surfaceCls = weeklyGuideSurfaceClassNames(cell.slotSurface);
                  return (
                    <td
                      key={`${period.label}-subject-${colIndex}`}
                      className={[
                        'cp-weekly-learning-guide-a4-cell',
                        'cp-weekly-learning-guide-a4-cell--center',
                        'cp-weekly-learning-guide-a4-cell--subject-line',
                        surfaceCls,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={
                        cell.slotSurface === 'class' && cell.subjectBgHex
                          ? { backgroundColor: cell.subjectBgHex }
                          : undefined
                      }
                    >
                      {cell.subject}
                    </td>
                  );
                })}
              </tr>
              <tr className="cp-weekly-learning-guide-a4-tr cp-weekly-learning-guide-a4-tr--1line">
                {period.cells.map((cell, colIndex) => {
                  const surfaceCls = weeklyGuideSurfaceClassNames(cell.slotSurface);
                  return (
                    <td
                      key={`${period.label}-unit-${colIndex}`}
                      className={[
                        'cp-weekly-learning-guide-a4-cell',
                        'cp-weekly-learning-guide-a4-cell--center',
                        cell.slotSurface === 'class' ? 'cp-weekly-learning-guide-a4-cell--unit' : '',
                        surfaceCls,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {cell.unit}
                    </td>
                  );
                })}
              </tr>
              <tr className="cp-weekly-learning-guide-a4-tr cp-weekly-learning-guide-a4-tr--learning">
                {period.cells.map((cell, colIndex) => {
                  const surfaceCls = weeklyGuideSurfaceClassNames(cell.slotSurface);
                  return (
                    <td
                      key={`${period.label}-learning-${colIndex}`}
                      className={['cp-weekly-learning-guide-a4-cell', 'cp-weekly-learning-guide-a4-cell--top', surfaceCls]
                        .filter(Boolean)
                        .join(' ')}
                      style={cell.slotSurface === 'class' ? subjectTintStyle(cell.subjectBgHex) : undefined}
                    >
                      <span className="cp-weekly-learning-guide-a4-cell-learning-inner">{cell.learning}</span>
                    </td>
                  );
                })}
              </tr>
              <tr className="cp-weekly-learning-guide-a4-tr cp-weekly-learning-guide-a4-tr--1line">
                {period.cells.map((cell, colIndex) => {
                  const surfaceCls = weeklyGuideSurfaceClassNames(cell.slotSurface);
                  return (
                    <td
                      key={`${period.label}-page-${colIndex}`}
                      className={['cp-weekly-learning-guide-a4-cell', 'cp-weekly-learning-guide-a4-cell--center', surfaceCls]
                        .filter(Boolean)
                        .join(' ')}
                      style={cell.slotSurface === 'class' ? subjectTintStyle(cell.subjectBgHex) : undefined}
                    >
                      {cell.page}
                    </td>
                  );
                })}
              </tr>
              <tr className="cp-weekly-learning-guide-a4-tr cp-weekly-learning-guide-a4-tr--1line">
                {period.cells.map((cell, colIndex) => {
                  const surfaceCls = weeklyGuideSurfaceClassNames(cell.slotSurface);
                  return (
                    <td
                      key={`${period.label}-material-${colIndex}`}
                      className={['cp-weekly-learning-guide-a4-cell', 'cp-weekly-learning-guide-a4-cell--center', surfaceCls]
                        .filter(Boolean)
                        .join(' ')}
                      style={cell.slotSurface === 'class' ? subjectTintStyle(cell.subjectBgHex) : undefined}
                    >
                      {cell.material}
                    </td>
                  );
                })}
              </tr>
            </>
          )}
        </tbody>
      ))}

      {includeNotice ? (
        <tbody className="cp-weekly-learning-guide-a4-notice-tbody">
          <tr className="cp-weekly-learning-guide-a4-tr cp-weekly-learning-guide-a4-tr--notice">
            <th className="cp-weekly-learning-guide-a4-period-title cp-weekly-learning-guide-a4-period-title--notice">
              가정통신문
            </th>
            <td colSpan={columns.length} className="cp-weekly-learning-guide-a4-notice-cell">
              <div className="cp-weekly-learning-guide-a4-notice-text">{noticeText}</div>
            </td>
          </tr>
        </tbody>
      ) : null}
    </table>
  );
}

type WeeklyLearningGuideA4WeekSheetProps = {
  block: WeeklyLearningGuideExportWeekBlock;
  neisMode: boolean;
  pageBreakAfter?: boolean;
};

function WeeklyLearningGuideA4WeekSheet({
  block,
  neisMode,
  pageBreakAfter = false,
}: WeeklyLearningGuideA4WeekSheetProps) {
  const { titleLine1, titleLine2, columns, periods, noticeText } = block;
  const layoutStyle = computeWeeklyGuideA4LayoutStyle({
    titleLine1,
    titleLine2,
    columns,
    periods,
    noticeText,
    neisMode,
  });
  const hasSecondTitle = titleLine2.trim().length > 0;

  return (
    <div
      className={
        'cp-weekly-learning-guide-a4-sheet' +
        (pageBreakAfter ? ' cp-weekly-learning-guide-a4-sheet--page-break' : '')
      }
      style={layoutStyle}
    >
      <header data-pdf-element="header" className="cp-weekly-learning-guide-a4-title">
        <p className="cp-weekly-learning-guide-a4-title-line1">{titleLine1}</p>
        {hasSecondTitle ? <p className="cp-weekly-learning-guide-a4-title-line2">{titleLine2}</p> : null}
      </header>

      <div className="cp-weekly-learning-guide-a4-table-wrap">
        <WeeklyLearningGuideA4WeekTable block={block} neisMode={neisMode} includeNotice={!neisMode} />
      </div>
    </div>
  );
}

type WeeklyLearningGuideA4NeisTermPageProps = {
  pageBlocks: WeeklyLearningGuideExportWeekBlock[];
  neisMode: boolean;
  pageBreakAfter?: boolean;
  layoutStyle: React.CSSProperties;
};

type WeeklyLearningGuideA4NeisGridColumnProps = {
  blocks: WeeklyLearningGuideExportWeekBlock[];
  neisMode: boolean;
  columnKey: string;
};

function WeeklyLearningGuideA4NeisGridColumn({
  blocks,
  neisMode,
  columnKey,
}: WeeklyLearningGuideA4NeisGridColumnProps) {
  return (
    <div className="cp-weekly-learning-guide-a4-neis-grid-col">
      {blocks.map((block, index) => (
        <div key={`${columnKey}-${block.titleLine1}-${index}`} className="cp-weekly-learning-guide-a4-neis-grid-cell">
          <p className="cp-weekly-learning-guide-a4-neis-grid-week-title">{block.titleLine1}</p>
          <div className="cp-weekly-learning-guide-a4-table-wrap cp-weekly-learning-guide-a4-table-wrap--grid">
            <WeeklyLearningGuideA4WeekTable block={block} neisMode={neisMode} includeNotice={false} />
          </div>
        </div>
      ))}
    </div>
  );
}

function WeeklyLearningGuideA4NeisTermPage({
  pageBlocks,
  neisMode,
  pageBreakAfter = false,
  layoutStyle,
}: WeeklyLearningGuideA4NeisTermPageProps) {
  const leftBlocks = pageBlocks.slice(0, 4);
  const rightBlocks = pageBlocks.slice(4, 8);

  return (
    <div
      className={
        'cp-weekly-learning-guide-a4-sheet cp-weekly-learning-guide-a4-sheet--neis-grid' +
        (pageBreakAfter ? ' cp-weekly-learning-guide-a4-sheet--page-break' : '')
      }
      style={layoutStyle}
    >
      <WeeklyLearningGuideNeisLegend className="cp-weekly-learning-guide-neis-legend--a4-export" />
      <div className="cp-weekly-learning-guide-a4-neis-grid">
        <WeeklyLearningGuideA4NeisGridColumn blocks={leftBlocks} neisMode={neisMode} columnKey="left" />
        <div className="cp-weekly-learning-guide-a4-neis-grid-column-divider" aria-hidden="true" />
        <WeeklyLearningGuideA4NeisGridColumn blocks={rightBlocks} neisMode={neisMode} columnKey="right" />
      </div>
    </div>
  );
}

const WeeklyLearningGuideA4Document = React.forwardRef<HTMLDivElement, WeeklyLearningGuideA4DocumentProps>(
  function WeeklyLearningGuideA4Document({ payload }, ref) {
    const { titleLine1, titleLine2, columns, periods, noticeText, neisMode, termWeekBlocks } = payload;

    if (termWeekBlocks && termWeekBlocks.length > 0 && neisMode) {
      const gridLayoutStyle = computeWeeklyGuideA4LayoutStyle(
        {
          titleLine1,
          titleLine2,
          columns,
          periods,
          noticeText,
          neisMode: true,
        },
        { gridCell: true, gridPageWithLegend: true }
      );
      const pages = chunkWeeklyGuideTermWeekBlocks(termWeekBlocks, NEIS_GRID_WEEKS_PER_PAGE);
      return (
        <div ref={ref} className="cp-weekly-learning-guide-a4-term-stack">
          {pages.map((pageBlocks, pageIdx) => (
            <WeeklyLearningGuideA4NeisTermPage
              key={`neis-term-page-${pageIdx}`}
              pageBlocks={pageBlocks}
              neisMode={neisMode}
              pageBreakAfter={pageIdx < pages.length - 1}
              layoutStyle={gridLayoutStyle}
            />
          ))}
        </div>
      );
    }

    if (termWeekBlocks && termWeekBlocks.length > 0) {
      return (
        <div ref={ref} className="cp-weekly-learning-guide-a4-term-stack">
          {termWeekBlocks.map((block, index) => (
            <WeeklyLearningGuideA4WeekSheet
              key={`${block.titleLine1}-${index}`}
              block={block}
              neisMode={neisMode}
              pageBreakAfter={index < termWeekBlocks.length - 1}
            />
          ))}
        </div>
      );
    }

    return (
      <div ref={ref} className="cp-weekly-learning-guide-a4-term-stack cp-weekly-learning-guide-a4-term-stack--single">
        <WeeklyLearningGuideA4WeekSheet
          block={{ titleLine1, titleLine2, columns, periods, noticeText }}
          neisMode={neisMode}
        />
      </div>
    );
  }
);

export default WeeklyLearningGuideA4Document;
